/**
 * What KIND of thing this is.
 *
 * Ten categories, derived from docs/product/taxonomy.md. That doc's table is
 * about the two TRACKS — expiry and restock — and this is the categorical spine
 * underneath it: the track a thing is on falls out of its category, so the
 * category is the one fact worth asking a model for.
 *
 * Deliberately NOT in this list: "never expires". Salt and sugar are staples
 * that happen to have no date, and a thing's shelf life is a property of the
 * date we hold, not of what kind of food it is. Making it a category would mean
 * sugar could not also be a staple, and the list would stop being a partition.
 */
export const CATEGORIES = [
    "dairy",
    "meat_fish",
    "produce",
    "bakery",
    "frozen",
    "staples",
    "snacks",
    "beverages",
    "condiments",
    "household",
];
/** How a category reads to a person. Shown; never the raw key. */
export const CATEGORY_LABEL = {
    dairy: "Dairy",
    meat_fish: "Meat, fish & eggs",
    produce: "Fruit & veg",
    bakery: "Bread & bakery",
    frozen: "Frozen",
    staples: "Staples",
    snacks: "Snacks",
    beverages: "Drinks",
    condiments: "Oil, masala & sauces",
    household: "Household",
};
/**
 * The emoji shown when nothing more specific is known.
 *
 * Every item gets a glyph from the moment it has a category, so a row is never
 * blank while enrichment is still in flight — and if enrichment never lands, the
 * fallback is indistinguishable from a deliberate choice.
 */
export const CATEGORY_EMOJI = {
    dairy: "🥛",
    meat_fish: "🍗",
    produce: "🥬",
    bakery: "🍞",
    frozen: "🧊",
    staples: "🌾",
    snacks: "🍪",
    beverages: "🥤",
    condiments: "🫙",
    household: "🧴",
};
/**
 * The last-resort glyph, for an item with no category at all.
 *
 * A parcel: it says "a thing we have not opened yet", which is exactly true of
 * an item nothing is known about.
 */
export const UNKNOWN_EMOJI = "📦";
/** Non-food. Asking for macros on detergent is a category error, not a miss. */
export const INEDIBLE = new Set(["household"]);
/** Is this something it makes sense to hold nutrition for? */
export function isEdible(c) {
    return c !== null && !INEDIBLE.has(c);
}
export function toCategory(raw) {
    if (typeof raw !== "string")
        return null;
    const v = raw
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
    return CATEGORIES.includes(v) ? v : null;
}
/**
 * The glyph to show for an item, best information first.
 *
 * A product-specific emoji beats its category's (🍫 is more use than 🍪), and
 * the category's beats nothing at all. One function so every surface — list,
 * detail, calendar — resolves it identically.
 */
export function itemEmoji(emoji, category) {
    if (emoji?.trim())
        return emoji.trim();
    if (category)
        return CATEGORY_EMOJI[category];
    return UNKNOWN_EMOJI;
}
/**
 * Guards what reaches the UI as an emoji.
 *
 * A model asked for "one emoji" can return a sentence, an empty string, or a
 * flag sequence. Anything that is not a short run of non-ASCII printable
 * characters is refused, because a row rendering the word "milk" where a glyph
 * belongs looks like a bug in a way a fallback glyph never does.
 *
 * Length is measured in CODE POINTS, not UTF-16 units: most food emoji are
 * surrogate pairs, and a couple (👨‍🍳) are ZWJ sequences several code points
 * long, so a naive `.length` would reject exactly the ones we want.
 */
export function coerceEmoji(raw) {
    if (typeof raw !== "string")
        return null;
    const s = raw.trim();
    if (!s)
        return null;
    const points = [...s];
    if (points.length > 6)
        return null;
    // Must contain at least one pictographic character, and no plain ASCII —
    // that combination rejects both prose and a bare letter.
    if (/[\x20-\x7E]/.test(s))
        return null;
    return /\p{Extended_Pictographic}/u.test(s) ? s : null;
}
//# sourceMappingURL=category.js.map